import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { mergeAttributes } from 'lib/commerce/cart/mergeAttributes';
import type { CartLineAttribute } from 'lib/commerce/cart/types';
import {
  TaxConfigKey,
  deleteFromLocalStorage,
  getFromLocalStorage,
  isCartMutateSuccess,
  saveToLocalStorage,
} from '../utils';
import { CartApiService } from '../services/CartApiService';
import { CartState } from './types';
import { CartHydrationState } from '../types';
import { GiftWrappingProductShopifyMap } from 'content/products/giftWrappingProduct';
import { ClueShopifyMap } from 'content/products/addons/clue';
import {
  SizingKitShopifyMap,
  RingProSizingKitShopifyMap,
} from 'content/products/ring';
import { RareSizingKitShopifyMap } from 'content/products/rare/productMap/constants';
import { SizingKitProductShopifyMap } from 'content/products/accessories/SizingKitProductShopifyMap';

export const useCartState = (): CartState => {
  const [cart, setCartInternal] = useState<Record<string, any> | null>(null);
  const [cartHydrationState, setCartHydrationState] =
    useState<CartHydrationState>('pending');
  const [store, setStoreState] = useState<ShopifyStore | null>(null);
  const [cartLoading, setCartLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const t = useTranslations('cart');

  const urlParams = useRef<Array<Record<string, string>> | null>(null);
  const isUpdating = useRef<boolean>(false);
  const cartStore = useRef<ShopifyStore | null>(null);
  const previousStore = useRef<ShopifyStore | null>(null);

  const setCart = useCallback(
    (newCart: Record<string, any> | null) => {
      setCartInternal(newCart);
      cartStore.current = newCart && store ? store : null;
    },
    [store],
  );

  const api = useMemo(() => (store ? new CartApiService(store) : null), [store]);

  const getCart = useCallback(
    async (cartId: string) => {
      if (!api || !cartId) return undefined;
      const data = await api.mutateCart({ cartId });
      if ('error' in data && data.error) return undefined;
      return data;
    },
    [api],
  );

  const applyAttributes = useCallback(
    async (cartId: string, attributes: CartLineAttribute[]) => {
      if (!api || !cartId) return undefined;
      const data = await api.mutateCart({ cartId, cartAttributes: attributes });
      if ('error' in data && data.error) return undefined;
      return data;
    },
    [api],
  );

  const updateCartAttributes = useCallback(
    (cartId: string, attributes: Array<Record<string, string>>): Promise<any> =>
      applyAttributes(cartId, attributes as unknown as CartLineAttribute[]),
    [applyAttributes],
  );

  const updateCart = useCallback(async () => {
    if (!store) return;
    setCartHydrationState('pending');
    setCart(null);

    const currentStoreCartKey = `cart_${store}`;
    const cartId_ls = getFromLocalStorage(currentStoreCartKey);
    if (!cartId_ls || ['', undefined, null].includes(cartId_ls)) {
      setCartHydrationState('ready');
      return;
    }

    try {
      const cartResponse = await getCart(cartId_ls);
      if (
        !cartResponse ||
        !isCartMutateSuccess(cartResponse) ||
        !cartResponse.cart?.id
      ) {
        deleteFromLocalStorage(currentStoreCartKey);
        setCart(null);
        setCartHydrationState('failed');
        return;
      }

      if (urlParams.current && urlParams.current.length > 0) {
        const merged = mergeAttributes(
          cartResponse.cart?.attributes ?? [],
          urlParams.current as unknown as CartLineAttribute[],
        );
        const updateResponse = await applyAttributes(cartId_ls, merged);
        if (
          !updateResponse ||
          !isCartMutateSuccess(updateResponse) ||
          !updateResponse.cart?.id
        ) {
          deleteFromLocalStorage(currentStoreCartKey);
          setCart(null);
          setCartHydrationState('failed');
        } else {
          setCart(updateResponse.cart);
          setCartHydrationState('ready');
        }
      } else {
        setCart(cartResponse.cart);
        setCartHydrationState('ready');
      }
    } catch (e) {
      console.error('Error updating cart:', e);
      deleteFromLocalStorage(currentStoreCartKey);
      setCart(null);
      setCartHydrationState('failed');
    }
  }, [store, getCart, applyAttributes, setCart]);

  const deleteCartFromLocalStorage = useCallback(() => {
    setCart(null);
    if (store) deleteFromLocalStorage(`cart_${store}`);
  }, [store, setCart]);

  const processUrlParameters = useCallback(() => {
    if (typeof window === 'undefined') return;
    const restrictedParams = [
      'currency',
      'type',
      'product',
      'affiliateDiscountCode',
      'affiliateName',
      'affiliateDiscountPercentage',
      'affiliateImage',
    ];
    const params = new URLSearchParams(window.location.search);
    const paramsArray: Array<Record<string, string>> = [];
    for (const [k, v] of params.entries()) {
      if (!restrictedParams.includes(k) && v) paramsArray.push({ key: k, value: v });
    }
    urlParams.current = paramsArray;
  }, []);

  useEffect(() => {
    if (isUpdating.current) return;
    processUrlParameters();

    if (cart && cart.id && store) {
      const currentStoreCartKey = `cart_${store}`;
      const cartId_ls = getFromLocalStorage(currentStoreCartKey);
      if (cartId_ls && urlParams.current && urlParams.current.length > 0) {
        isUpdating.current = true;
        getCart(cartId_ls)
          .then((cartResponse) => {
            if (
              !cartResponse ||
              !isCartMutateSuccess(cartResponse) ||
              !cartResponse.cart?.id
            ) {
              deleteFromLocalStorage(currentStoreCartKey);
              setCart(null);
              return;
            }
            const merged = mergeAttributes(
              cartResponse.cart?.attributes ?? [],
              urlParams.current as unknown as CartLineAttribute[],
            );
            return applyAttributes(cartId_ls, merged);
          })
          .then((response) => {
            if (!response) return;
            if (!isCartMutateSuccess(response) || !response.cart?.id) {
              deleteFromLocalStorage(currentStoreCartKey);
              setCart(null);
            } else {
              setCart(response.cart);
            }
          })
          .catch((e) => {
            console.error('Error updating cart attributes from URL params:', e);
            deleteFromLocalStorage(currentStoreCartKey);
            setCart(null);
          })
          .finally(() => {
            isUpdating.current = false;
          });
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (store && previousStore.current !== store) {
      setCart(null);
      isUpdating.current = false;
      previousStore.current = store;
      updateCart();
    }
  }, [store]);

  useEffect(() => {
    processUrlParameters();
    updateCart();
  }, []);

  useEffect(() => {
    if (cart && 'id' in cart && store && cartStore.current === store) {
      saveToLocalStorage(`cart_${store}`, cart.id);
    }
  }, [cart, store]);

  // Paid standalone kit excluded by id, not by omission: outside PRODUCTION
  // RingProSizingKitShopifyMap aliases it, and it is a legit solo buy.
  const nonStandaloneProductIds = useMemo(() => {
    if (!store) return [];
    const paidKitId =
      SizingKitProductShopifyMap[store as ShopifyStore]?.shopifyVariantId;
    return [
      GiftWrappingProductShopifyMap[store as ShopifyStore]?.variantId,
      SizingKitShopifyMap[store as ShopifyStore]?.shopifyVariantId,
      RingProSizingKitShopifyMap[store as ShopifyStore]?.shopifyVariantId,
      RareSizingKitShopifyMap[store as ShopifyStore]?.shopifyVariantId,
      ClueShopifyMap[store as ShopifyStore]?.Year1?.variantId,
    ].filter((id) => Boolean(id) && id !== paidKitId);
  }, [store]);

  // Empty cart is deliberately NOT "only accessories" — .every() would say true.
  const hasOnlyNonStandaloneItems = useMemo(() => {
    const lines = cart?.lines?.edges ?? [];
    if (lines.length === 0) return false;
    return lines.every((edge: { node: { merchandise: { id: string } } }) =>
      nonStandaloneProductIds.includes(edge.node.merchandise.id),
    );
  }, [cart, nonStandaloneProductIds]);

  useEffect(() => {
    if (!cart || !store) return;
    const isEmpty = (cart.lines?.edges ?? []).length === 0;
    if (isEmpty || hasOnlyNonStandaloneItems) deleteCartFromLocalStorage();
  }, [cart, store, hasOnlyNonStandaloneItems, deleteCartFromLocalStorage]);

  const computedValues = useMemo(() => {
    const taxKey = TaxConfigKey[store as ShopifyStore];
    return {
      taxes: cart?.cost?.totalTaxAmount?.amount ?? 0,
      duty: cart?.cost?.totalDutyAmount?.amount ?? 0,
      taxString: taxKey ? t(taxKey) : '',
    };
  }, [cart?.cost, store, t]);

  return {
    cart,
    cartHydrationState,
    setCart,
    store,
    setStore: setStoreState,
    cartLoading,
    setCartLoading,
    error,
    setError,
    deleteCartFromLocalStorage,
    updateCart,
    updateCartAttributes,
    urlParams: urlParams.current,
    hasOnlyNonStandaloneItems,
    ...computedValues,
  };
};
